import React, { useContext, useEffect, useMemo, useState } from "react";
import { Affix, Button, Col, Icon, message, Row, Tooltip } from "antd";
import { errorMessage, QuestionType, sectionDescribe } from "service/service";
import { countTotalScore, saveChooseTopic } from "service/homework";
import PaperContext from "../../context";
import arrayMove from "array-move";
import WidgetQuestBlock from "../WidgetQuestBlock";
import {
    SortableContainer,
    SortableElement,
    SortableHandle
} from "react-sortable-hoc";
import Widget from "components/Widget";
import style from "../../../../exam/addExam/components/ExamEditWidget/ExamEditWidget.module.scss";
import useModalHandler from "../../../../../utils/v2/useModalHandler";
import chooseAnswer from "../../../../answerStore/MyStore/components/SelectModal/ChooseAnswer";
import useRequestFn from "../../../../../utils/useRequestFn";
import { QuestionListItem } from "../../../../../service/answerStore";
import { ResultMessage } from "../../../../../components/ResultMessage";
import ChooseAnswer from "../../../../answerStore/MyStore/components/SelectModal/ChooseAnswer";
import classNames from "classnames";
import CopyModal from "../HomeworkEditWidget/Components/CopyModal";

export const AllTopicType = [
    QuestionType.DanXuan,
    QuestionType.DuoXuan,
    QuestionType.PanDuan,
    QuestionType.ZhuGuan,
    QuestionType.TianKong,
    QuestionType.TingLi,
    QuestionType.WanXingTianKong,
    QuestionType.YueDuLiJie
];

interface ExamEditWidgetProps {
    examName: string;
    examTypeList: QuestionType[];
    sortType: (topicTypes: QuestionType[], sort: boolean) => void;
    children?: React.ReactNode;
}

export default function ExamEditWidget({
    examName,
    examTypeList,
    sortType,
    children
}: ExamEditWidgetProps) {
    const { topics, lockEdit, paperParams, loadPaperData, paperId } =
        useContext(PaperContext);
    const [activePanel, setActivePanel] = useState("");
    const totalScore = useMemo(() => countTotalScore(topics), [topics]);

    const [topicTypes, setTopicTypes] = useState(examTypeList);

    useEffect(() => {
        setTopicTypes(examTypeList);
    }, [examTypeList]);

    const onToggle = (active: boolean, type: QuestionType) => {
        setActivePanel(active ? type : "");
    };

    const handleSortType = ({
        oldIndex,
        newIndex
    }: {
        oldIndex: number;
        newIndex: number;
    }) => {
        const array = arrayMove(topicTypes, oldIndex, newIndex);
        setTopicTypes(array);
        sortType(array, true);
    };

    const filterTopicByType = (type: QuestionType) => {
        return topics.filter((item: any) => item.topicType === type);
    };

    const DragHandle = SortableHandle(() => (
        <Tooltip title="鼠标左键点击图标拖拽调整顺序" className="drag">
            <Icon type="drag" />
        </Tooltip>
    ));

    const SortableItem = SortableElement(
        ({ type, className }: { type: QuestionType; className?: string }) => {
            return (
                <div className={className}>
                    <WidgetQuestBlock
                        end={<DragHandle />}
                        list={filterTopicByType(type)}
                        type={type}
                        active={activePanel === type}
                        onToggle={active => onToggle(active, type)}
                    />
                </div>
            );
        }
    );

    const SortableList = useMemo(() => {
        return SortableContainer(({ list }: { list: QuestionType[] }) => {
            return (
                <div>
                    {list.map((type, index) => (
                        <SortableItem
                            className={style.sortableitem}
                            key={type}
                            index={index}
                            type={type}
                        />
                    ))}
                </div>
            );
        });
    }, [topicTypes]);
    const chooseAnswerModal = useModalHandler(chooseAnswer, { remove: true });
    const [savaData] = useRequestFn(saveChooseTopic);
    async function saveToHomeWork(data: QuestionListItem[]) {
        try {
            await savaData({
                paperId,
                weekTimeId: paperParams.weekTimeId,
                data: data.map(item => {
                    return {
                        questionId: item.questionId,
                        score: item.scope || 0
                    };
                })
            });
            message.success("选题成功");
            chooseAnswerModal.close();
            await loadPaperData();
        } catch (e) {
            ResultMessage.error(errorMessage(e));
        }
    }
    const copyModal = useModalHandler(undefined, { remove: true });
    const onCopy = () => {
        copyModal.open();
    };
    return (
        <Affix>
            <Widget title="作业信息" bodyPadding>
                <div className={style.root}>
                    <Row className={style.item}>
                        <Col span={6} className={style.label}>
                            课程：
                        </Col>
                        <Col className={style.title} span={18}>
                            {examName}
                        </Col>
                    </Row>

                    <Row className={style.item}>
                        <Col span={6} className={style.label}>
                            周次：
                        </Col>
                        <Col span={6}>第{paperParams.weekTime}周</Col>
                        <Col span={4} className={style.label}>
                            节次：
                        </Col>
                        <Col span={8}>
                            {sectionDescribe(
                                paperParams.section,
                                paperParams.continunity
                            )}
                        </Col>
                    </Row>

                    <Row className={style.item}>
                        <Col span={6} className={style.label}>
                            总分：
                        </Col>
                        <Col span={6}>{totalScore}分</Col>
                        <Col span={4} className={style.label}>
                            题数：
                        </Col>
                        <Col span={8}>{topics.length}</Col>
                    </Row>

                    <Row
                        type="flex"
                        justify="end"
                        gutter={15}
                        style={{ marginBottom: 15 }}>
                        <Col span={10}>
                            <Button
                                type="primary"
                                size="small"
                                ghost
                                block
                                onClick={chooseAnswerModal.open}
                                disabled={lockEdit}>
                                <Icon type="check-square" /> 题库选题
                            </Button>
                        </Col>
                        <Col span={10}>
                            <Button
                                type="primary"
                                ghost
                                block
                                size="small"
                                onClick={onCopy}
                                disabled={lockEdit}>
                                <Icon type="copy" /> 复制作业
                            </Button>
                        </Col>
                    </Row>

                    <Row>
                        <div className={classNames(lockEdit && style.lock)}>
                            <SortableList
                                useDragHandle
                                onSortEnd={handleSortType}
                                lockAxis="y"
                                list={topicTypes}
                            />
                        </div>
                    </Row>
                </div>

                <div>{children}</div>

                {chooseAnswerModal.render(
                    <ChooseAnswer
                        onConfirm={saveToHomeWork}
                        onCancel={chooseAnswerModal.close}
                    />
                )}
                {copyModal.render(<CopyModal onClose={copyModal.close} />)}
            </Widget>
        </Affix>
    );
}
